import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface UnsavedChangesBarProps {
  /** Whether there are unsaved changes to show the bar for */
  isDirty: boolean;
  /** Callback to save the current changes */
  onSave: () => void;
  /** Callback to discard and revert changes */
  onDiscard: () => void;
  /** Whether a save is currently in progress */
  isSaving: boolean;
}

/**
 * Reserves space at the bottom of a settings panel and shows a sticky save/discard bar
 * when there are unsaved changes. Always renders a fixed-height element so the page
 * layout remains stable whether or not the bar is visible.
 * @param props - UnsavedChangesBarProps
 * @returns Sticky unsaved changes bar or an invisible spacer of equal height
 */
export function UnsavedChangesBar({
  isDirty,
  onSave,
  onDiscard,
  isSaving,
}: UnsavedChangesBarProps) {
  if (!isDirty) {
    return <div className="h-14" />;
  }

  return (
    <div className="sticky bottom-0 z-10 rounded-lg border bg-muted/40 backdrop-blur supports-[backdrop-filter]:bg-muted/30 px-4 py-3 shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-warning" />
          <p className="text-sm font-medium text-muted-foreground">
            Unsaved changes
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onDiscard}
            disabled={isSaving}
          >
            Discard
          </Button>
          <Button size="sm" onClick={onSave} disabled={isSaving}>
            Save Changes
            {isSaving && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
